import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Printer, Phone, MessageCircle, AlertTriangle } from "lucide-react";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { getBusinessConfig } from "@/lib/site-config";

const QUICK_CHECKS: string[] = [
  "All headlights, sidelights, indicators and brake lights working",
  "Number plate lights illuminate",
  "Tyres above 1.6mm tread (use the 20p test)",
  "No bulges, cuts or visible cord on any tyre sidewall",
  "Windscreen free of cracks larger than 10mm in the driver's view",
  "Wipers don't streak; washer fluid topped up to MAX",
  "Horn works and is clearly audible",
  "All mirrors present and intact",
  "Both number plates secure, legal font and unfaded",
  "Driver and passenger seatbelts retract and lock cleanly",
  "No dashboard warning lights illuminated when running",
  "At least 1/4 tank of fuel for emissions test",
  "Boot empty enough to inspect floor and spare-wheel well",
];

const DRIVEWAY_STEPS: { title: string; body: string }[] = [
  { title: "1. Walk round the car", body: "Look for kerb damage on the wheels, scuffed bumpers, cracked light units and any tyre sidewall lumps. Two minutes here saves a £200 fail." },
  { title: "2. Lights and indicators", body: "Sit a friend in the driver's seat or use a wall reflection. Cycle through dipped, main beam, hazards, indicators, brake, reverse and fogs. Replace blown bulbs — they're under a fiver." },
  { title: "3. Tyres and tread", body: "Slot a 20p coin into each main groove. If you can see the outer band, that tyre is at the legal limit. Check pressures cold against the door-shut sticker." },
  { title: "4. Wipers and washers", body: "Run the wipers across a wet screen. Streaks, judder or torn rubber means new blades. Top washer fluid to MAX — empty bottle is an automatic fail." },
  { title: "5. Inside the cabin", body: "Test horn, seatbelts (sharp tug to confirm lock), all mirrors, and check no warning lights stay on after the engine starts." },
  { title: "6. Under the bonnet", body: "Engine oil between MIN and MAX, coolant in the expansion tank to the cold line, brake fluid above MIN. No fresh leaks on the ground where the car sits overnight." },
];

const COMMON_FAILS: string[] = [
  "Tyres below legal tread or sidewall damage",
  "Failed bulbs (headlight, brake, number plate)",
  "Ineffective wipers or empty washer bottle",
  "Windscreen chip larger than 10mm in driver's view",
  "Brake imbalance or worn pads/discs",
  "Suspension play — top mounts, ball joints, anti-roll links",
  "Emissions over limit (commonly EGR or DPF on diesels)",
  "Dashboard warning lights (engine, ABS, airbag, brake)",
  "Number plate fading, incorrect font or insecure fixing",
  "Excessive steering play or worn track-rod ends",
];

const WARNING_SIGNS: string[] = [
  "Squeaking or grinding brakes",
  "Car pulling to one side when braking or steering",
  "Uneven tyre wear (inner or outer edge worn)",
  "Knocking or clonking over speed bumps",
  "Blue, white or black smoke from the exhaust",
  "Engine management light on (solid or flashing)",
  "ABS or airbag warning light on",
  "Weak handbrake (won't hold on a slope)",
  "Excessive steering wheel vibration at 50–70mph",
  "Fluid leaks under the car after standing overnight",
];

const FAQS = [
  { q: "How early can I book my MOT?", a: "Up to 28 days before the current certificate expires without losing any days — the new certificate runs from the old expiry date." },
  { q: "Will a dashboard warning light fail my MOT?", a: "In most cases, yes. Engine management, ABS, airbag, ESC and brake-system warnings are automatic major defects since May 2018." },
  { q: "Do you charge for re-tests?", a: "If we carry out the repair within the DVSA-allowed 10-working-day window, the partial re-test is free." },
  { q: "Can I drive my car if it fails the MOT?", a: "Only if the previous certificate is still valid AND there are no 'dangerous' defects. If the old MOT has expired, the only legal trip is to a pre-arranged repair or re-test appointment." },
  { q: "Do I need to bring the V5C logbook?", a: "No — the test station looks the vehicle up by registration. Bring it if you have any details to verify." },
  { q: "What happens if my car fails?", a: "We tell you exactly what failed and why, quote in writing before any repair, fit reputable parts, and aim for same-day completion where possible. No scare tactics, no padding." },
];

export const Route = createFileRoute("/mot-checklist-before-test")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/mot-checklist-before-test",
      title: "MOT Checklist Before Your Test | Pass First Time | Woodseer Autos E14",
      description:
        "Free MOT pre-test checklist from a DVSA-authorised garage in Poplar, East London. Lights, tyres, brakes, wipers and warning lights — what to check before your MOT.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Motorist Help", path: "/motorist-help" },
        { name: "MOT Checklist Before Test", path: "/mot-checklist-before-test" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "MOT Testing — Woodseer Autos",
          serviceType: "MOT Test",
          description: "DVSA-authorised MOT testing (classes 1, 2, 3, 4, 5, 7) in Poplar, East London.",
          areaServed: ["Poplar", "Canary Wharf", "Isle of Dogs", "Limehouse", "Tower Hamlets", "East London"],
          url: "https://woodseerautos.co.uk/mot",
        }),
        faqSchema(FAQS) as Record<string, unknown>,
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to prepare your car for an MOT",
          description: "Six driveway checks every driver can do in fifteen minutes to dramatically improve the chance of a first-time MOT pass.",
          step: DRIVEWAY_STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title.replace(/^\d+\.\s*/, ""),
            text: s.body,
          })),
        },
      ],
    }),
  component: MotChecklistBeforeTestPage,
});

function MotChecklistBeforeTestPage() {
  const cfg = getBusinessConfig();
  return (
    <>
      {/* Print-only styles — hide chrome, render a clean A4-friendly page */}
      <style>{`
        @media print {
          header, nav, footer, .mobile-bottom-bar, [data-print-hide], aside,
          .whatsapp-fab, .cookie-banner, .review-cta-strip { display: none !important; }
          body { background: #fff !important; color: #000 !important; }
          .print-only { display: block !important; }
          .checklist-print-block, .checklist-print-block * { color: #000 !important; background: #fff !important; }
          .checklist-print-block { border: 1px solid #000 !important; padding: 16px !important; }
          a { color: #000 !important; text-decoration: none !important; }
          h1, h2, h3 { page-break-after: avoid; }
          li { page-break-inside: avoid; }
        }
        .print-only { display: none; }
      `}</style>

      <section className="py-14 md:py-20 bg-secondary" data-print-hide>
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">MOT Checklist Before Your Test</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight">Pass your MOT first time</h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Simple checks any driver can do before arriving at Woodseer Autos. Built from the same things our DVSA-authorised testers flag most often — and the things drivers fix in five minutes for almost no money.
          </p>
          <div className="mt-6 inline-flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            If a dashboard warning light is on, get it diagnosed first — it's an automatic fail in most cases.
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => { if (typeof window !== "undefined") window.print(); }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"
            >
              <Printer className="h-4 w-4" />Print checklist
            </button>
            <a href={cfg.phoneHref} className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground">
              <Phone className="h-4 w-4" />Call {cfg.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Printable checklist block */}
      <section className="py-10 md:py-12">
        <div className="mx-auto max-w-3xl px-4">
          <div className="checklist-print-block rounded-2xl border border-border bg-card p-6">
            <div className="print-only" style={{ marginBottom: 12 }}>
              <div role="heading" aria-level={2} style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Woodseer Autos — MOT Pre-Test Checklist</div>
              <p style={{ fontSize: 11, margin: "4px 0 0" }}>
                {cfg.name} · {cfg.address}, {cfg.postcode} · {cfg.phone} · woodseerautos.co.uk
              </p>
              <p style={{ fontSize: 11, margin: "4px 0 0", fontWeight: 700 }}>
                Need help before your MOT? Call {cfg.phone}
              </p>
            </div>
            <h2 className="text-xl font-bold text-foreground">Quick pre-MOT checklist</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
              {QUICK_CHECKS.map((c) => (
                <li key={c} className="flex items-start gap-2 text-muted-foreground">
                  <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-border" aria-hidden />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 bg-secondary">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-2xl font-bold text-foreground">10-minute driveway MOT preparation</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {DRIVEWAY_STEPS.map((s) => (
              <div key={s.title} className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-bold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12">
        <div className="mx-auto max-w-3xl px-4 grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">Common reasons cars fail MOTs</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {COMMON_FAILS.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{f}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Tell-tale signs to fix before the test</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {WARNING_SIGNS.map((w) => <li key={w} className="flex gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{w}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 bg-secondary">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-xl font-bold text-foreground">What to bring or prepare</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
            {["Vehicle registration", "Any known issue (note it on your phone)", "Service history if available", "Locking wheel-nut key", "At least 1/4 tank of fuel", "Best contact phone number"].map((x) => (
              <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{x}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-10 md:py-12">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-xl font-bold text-foreground">If your vehicle fails — here's what happens</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            We explain exactly what failed and why, in plain English. You'll get a written quote before any repair starts, with fair pricing and reputable parts. Same-day repairs are common; the partial re-test is free if we carry the work out within the DVSA's 10-working-day window. No scare tactics — and we'll tell you when something genuinely isn't worth fixing on an older car.
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-print-hide>
            <a href={cfg.phoneHref} className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"><Phone className="h-4 w-4" />Call {cfg.phone}</a>
            <a href={cfg.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"><MessageCircle className="h-4 w-4" />WhatsApp</a>
            <Link to="/book" search={{ service: "mot" }} className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground">Request MOT booking</Link>
            <Link to="/motorist-help" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground">More motorist guides</Link>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={FAQS} title="MOT preparation FAQs" />
    </>
  );
}
